<template>
  <div id="default-flashcard-wrapper">
    <calculator-modal
      v-if="showCalculator"
      @close="showCalculator = false"
    ></calculator-modal>
    <page-layout>
      <template #header>
        <simulation-header
          :title="simulationName"
          :subtitle="userName"
          :time-left-in-seconds="timeLeftInSeconds"
          :question-number="flashcardIndex + 1"
          :total-questions="flashcards.length"
          :include-calculator="true"
          @calculator-clicked="showCalculator = !showCalculator"
        ></simulation-header>
      </template>
      <div
        class="flashcard-content two-column"
        data-test="flashcard-content"
      >
        <div
          class="first-column"
        >
          <scenario-box :flashcard-id="flashcard.id"></scenario-box>
          <asset-tray
            :flashcard="flashcard"
          ></asset-tray>
        </div>
        <flashcard-body
          :flashcard="flashcard"
          :flashcard-state="flashcardState"
          @answers-selected="answersSelected"
          @set-raw-answer-data="setRawAnswerData"
        ></flashcard-body>
      </div>
      <template #footer>
        <page-footer
          :button-text="footerButtonText"
          :show-previous-button="showPreviousButton"
          @next-button-clicked="gotoNextFlashcard"
          @previous-button-clicked="gotoPreviousFlashcard"
        >
          <template v-if="hasFilter" #left-buttons>
            <button class="next-button" @click="goToReview">
              <span>Return to Review</span>
              <icon
                name="line-arrow-right"
                variant="white"
                class="next-button-icon rotate-icon-up"
              ></icon>
            </button>
          </template>
          <template
            v-if="allowBookmarks"
            #secondary
          >
            <bookmark-flag
              :has-bookmark="hasBookmark"
              @toggle-bookmark="toggleBookmark"
            ></bookmark-flag>
          </template>
        </page-footer>
      </template>
    </page-layout>
    <response-required-modal
      v-if="showResponseRequired"
      @close="showResponseRequired = false"
    />
  </div>
</template>

<script>
/**
 * This is the flashcard component for simulations with in the style of the nursing simulations.
 * It is used for both single-section and multi-section simulations.
 * It is not used for the Dental simulations.
 */
import PageLayout from '@components/sims/single-section-sim/PageLayout.vue';
import PageFooter from '@components/sims/single-section-sim/PageFooter.vue';
import BookmarkFlag from '@components/sims/single-section-sim/BookmarkFlag.vue';
import SimulationHeader from '@components/sims/single-section-sim/SimulationHeader.vue';
import FlashcardBody from '@components/sims/single-section-sim/FlashcardBody.vue';
import WuxSimulationsMixin from '@/mixins/wux-simulations';
import BookmarksMixin from '@/mixins/bookmarks';
import CalculatorModal from '@/components/calculator/CalculatorModal.vue';
import { arraysEquivalent } from '@/utils/arrays';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import AccommodationsMixin from '@/mixins/accommodations';
import AssetTray from '@components/sims/single-section-sim/AssetTray.vue';
import ScenarioBox from '@components/sims/single-section-sim/ScenarioBox.vue';
import ResponseRequiredModal from '@components/sims/single-section-sim/ResponseRequiredModal.vue';
import Icon from '@components/singletons/icon';
import _ from 'lodash';

// temporary until we have a better way to determine supported flashcard types
const supportedFlashcardTypes = [
  'Multiple Choice',
  'Numeric Entry',
  'Ordered Response',
  'SATA',
  'Drag and Drop',
  'Bowtie',
  'Matrix Grid',
  'Highlight',
  'Cloze / Dropdown',
  'Hotspot',
];
const flashcardTypesWithRawAnswerData = ['Numeric Entry', 'Ordered Response', 'Matrix Grid', 'Hotspot'];

export default {
  name: 'DefaultFlashcard',
  components: {
    AssetTray,
    BookmarkFlag,
    PageLayout,
    FlashcardBody,
    PageFooter,
    ScenarioBox,
    SimulationHeader,
    CalculatorModal,
    ResponseRequiredModal,
    Icon,
  },
  mixins: [WuxSimulationsMixin, FlashcardViewMixin, BookmarksMixin, AccommodationsMixin],
  data() {
    return {
      timeLeftInSeconds: null,
      timerInterval: null,
      showCalculator: false,
      answerIds: [],
      rawAnswerData: {},
      showResponseRequired: false,
    };
  },
  computed: {
    allAnswerIds() {
      return this.flashcard?.answers?.map(a => a.id) || [];
    },
    allowBookmarks() {
      return this.simConfig?.allowBookmarks;
    },
    currentCategoryId() {
      return this.isMultiSectionSimulation ? this.section.id : this.simId;
    },
    filtered() {
      return this.$route.query?.filter;
    },
    flashcard() {
      return this.flashcards[this.flashcardIndex] || {};
    },
    flashcardId() {
      return this.$route.params['flashcardId'];
    },
    flashcardIndex() {
      return this.flashcards.findIndex( f => f.id == this.flashcardId );
    },
    flashcardSetIndex() {
      return this.flashcardSetIds.findIndex( id => id == this.flashcardId );
    },
    flashcardSetIds() {
      return this.$store.getters['flashcardSet/ids'];
    },
    flashcardState() {
      return this.getFlashcardState(this.flashcardId, this.currentCategoryId);
    },
    footerButtonText() {
      if (this.nextFlashcardId) {
        return 'Next';
      }
      if (this.allowBookmarks && this.isMultiSectionSimulation) {
        return 'Section Review';
      }
      return 'Finish';
    },
    hasFilter() {
      return !!this.$route.query?.filter;
    },
    nextFlashcardId() {
      if (this.filtered) {
        return this.flashcardSetIds[this.flashcardSetIndex + 1];
      }

      if (this.flashcardIndex >= this.flashcards.length - 1) return null;
      return this.flashcards[this.flashcardIndex + 1].id;
    },
    previousFlashcardId() {
      if (this.filtered) {
        return this.flashcardSetIds[this.flashcardSetIndex - 1];
      }

      if (this.flashcardIndex <= 0) return null;
      return this.flashcards[this.flashcardIndex - 1].id;
    },
    showPreviousButton() {
      return !!this.previousFlashcardId;
    },
    simulationName() {
      if (!this.isMultiSectionSimulation) {
        return this.simulation?.name || '';
      }
      return `${this.simulation?.name} - ${this.section?.name}`;
    },
    timeAllowedInMinutes() {
      if (this.isMultiSectionSimulation) {
        return this.section.timeInMinutes;
      }
      return this.simConfig?.timeInMinutes;
    },
    troubleshooting() {
      return {
        flashcard: this.flashcard,
      };
    },
    userName() {
      const user = this.$store.getters['user/get'];
      const firstName = user?.firstName || '';
      const lastName = user?.lastName || '';
      return `${firstName} ${lastName}`.trim();
    },
    usesRawAnswerData() {
      return flashcardTypesWithRawAnswerData.includes(this.flashcard.type);
    },
    isCurrentAnswered() {
  if (this.usesRawAnswerData) {
    if (this.flashcard?.type === 'Matrix Grid') {
      const rows = this.rawAnswerData?.matrixGridSelections;
      return Array.isArray(rows) && rows.some(
        r => r && Array.isArray(r.selections) && r.selections.some(Boolean),
      );
    }
    return !_.isEmpty(this.rawAnswerData);
  }

  return Array.isArray(this.answerIds) && this.answerIds.length > 0;
},
    disallowQuestionSkipping() {
    const sectionFlag = this.section?.disallowQuestionSkipping;
    const simFlag = this.simConfig?.disallowQuestionSkipping;
      return Boolean(sectionFlag ?? simFlag ?? false);
    },
    shouldBlockNext() {
      return this.disallowQuestionSkipping && !this.isCurrentAnswered;
    },
},
  watch: {
    flashcard(newState, oldState) {
      if (newState.id !== oldState.id) {
        this.resetAnswer();
        this.showResponseRequired = false;
      }
    },
  },
  mounted() {
    this.timerInterval = setInterval(() => {
      try {
        const timeLeft = this.calculateTimeLeft();
        if (timeLeft < 0) {
          clearInterval(this.timerInterval);
          this.submitSection();
        }
        this.timeLeftInSeconds = timeLeft;
      }
      catch {
        clearInterval(this.timerInterval);
      }
    }, 250);

    this.resetAnswer();
  },
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  },
  methods: {
    answersSelected(answerIds) {
      this.answerIds = Array.isArray(answerIds) ? [...answerIds] : [];
      this.updateAnswer();
    },
    calculateTimeLeft() {
      // Get the correct category state based on simulation type
      const categoryState = this.isMultiSectionSimulation
        ? this.categoryState
        : this.categoryStatesFiltered[0];

      if (!categoryState?.mobileCreatedAt) return null;
      if (this.timeAllowedInMinutes === 0) return null;

      let simTimeInMinutes = 0;
      if (this.isMultiSectionSimulation) {
        simTimeInMinutes = this.section.timeInMinutes;
      } else {
        simTimeInMinutes = this.getTimeByCategory(this.simConfig.categoryId) || this.simConfig.timeInMinutes;
      }
      return this.timeLeft(
        categoryState.mobileCreatedAt,
        simTimeInMinutes,
      ) / 1000;
    },
    gotoNextFlashcard() {
      if (this.shouldBlockNext) {
        this.showResponseRequired = true;
        return;
      }

      this.updateAnswer();

      const query = (this.filtered && this.flashcardSetIds.includes(this.nextFlashcardId)) ? { filter: this.filtered } : {};

      if (this.isMultiSectionSimulation) {
        if (this.nextFlashcardId) {
          this.$router.push({
            name: 'simSectionFlashcard#show',
            params: {
              simId: this.simulation.id,
              sectionId: this.section.id,
              flashcardId: this.nextFlashcardId,
            },
            query,
          });
        } else if (this.allowBookmarks) {
          this.$router.push({
            name: 'simSection#review',
            params: {
              simId: this.simulation.id,
              sectionId: this.section.id,
            },
            query,
          });
        } else {
          this.submitSection();
        }
      }
      else {
        if (this.nextFlashcardId) {
          this.$router.push({
            name: 'simFlashcard#show',
            params: {
              simId: this.simulation.id,
              flashcardId: this.nextFlashcardId,
            },
            query,
          });
        } else if (this.allowBookmarks) {
          this.$router.push({
            name: 'sims#review',
            params: {
              simId: this.simulation.id,
              sectionId: this.section.id,
            },
          });
        } else {
          this.$router.push({
            name: 'sims#outro',
            params: {
              simId: this.simulation.id,
            },
          });
        }
      }
    },
    gotoPreviousFlashcard() {
      this.updateAnswer();

      const query = (this.filtered && this.flashcardSetIds.includes(this.previousFlashcardId)) ? { filter: this.filtered } : {};

      if (this.isMultiSectionSimulation) {
        this.$router.push({
          name: 'simSectionFlashcard#show',
          params: {
            simId: this.simulation.id,
            sectionId: this.section.id,
            flashcardId: this.previousFlashcardId,
          },
          query,
        });
      } else {
        this.$router.push({
          name: 'simFlashcard#show',
          params: {
            simId: this.simulation.id,
            flashcardId: this.previousFlashcardId,
          },
          query,
        });
      }
    },
    goToReview() {
      if (this.isMultiSectionSimulation) {
        this.$router.push({
          name: 'simSection#review',
          params: {
            simId: this.simulation.id,
            sectionId: this.section.id,
          },
        });
      } else {
        this.$router.push({
          name: 'sims#review',
          params: {
            simId: this.simulation.id,
          },
        });
      }
    },
    isAnswerIdValid(answerId) {
      return this.allAnswerIds.includes(answerId);
    },
    resetAnswer() {
      if (this.usesRawAnswerData) {
        this.rawAnswerData = this.flashcardState.rawAnswerData || {};
        this.answerIds = [];
      } else {
        this.rawAnswerData = {};
        this.answerIds = Array.isArray(this.flashcardState?.answerIds)
         ? [...this.flashcardState.answerIds]
         : [];
      }
    },
    setRawAnswerData(rawAnswerData) {
      this.rawAnswerData = rawAnswerData;
      this.updateAnswer();
    },
    updateAnswer() {
      if (!supportedFlashcardTypes.includes(this.flashcard.type)) {
        console.error(`Flashcard type ${this.flashcard.type} is not supported`);
        console.error('Supported flashcard types are: ', supportedFlashcardTypes);
        return;
      }

      if (this.calculateTimeLeft() < 0) return false;

      if (this.usesRawAnswerData) {
        return this.updateAnswerWithRawAnswerData();
      } else {
        return this.updateAnswerWithAnswerIds();
      }
    },
    updateAnswerWithAnswerIds() {
      const hasSavedAnswer = Array.isArray(this.flashcardState?.answerIds);

      if (hasSavedAnswer && arraysEquivalent(this.flashcardState.answerIds, this.answerIds)) {
        return false;
      }

      if (this.answerIds.some(id => !this.isAnswerIdValid(id))) {
        return false;
      }

      const correct = this.evaluateAnswer(this.flashcard, this.answerIds, {});

      const flashcardStatusId = (this.answerIds.length > 0)
        ? this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id
        : this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'].id;

      const params = {
        correct: correct,
        flashcardId: this.flashcard.id,
        flashcardStatusId,
        categoryId: this.currentCategoryId,
        answerIds: this.answerIds,
      };

      this.createFlashcardState(params);
      this.$analytics.events.updateSimulationAnswer({
        simulation: this.simulation,
        section: this.section,
        flashcard: this.flashcard,
        answerIds: this.answerIds,
      });
      return true;
    },
    updateAnswerWithRawAnswerData() {
      const hasSavedAnswer = this.flashcardState?.rawAnswerData && !_.isEmpty(this.flashcardState.rawAnswerData);

      if (hasSavedAnswer && _.isEqual(this.flashcardState.rawAnswerData, this.rawAnswerData)) {
        return false;
      }

      const correct = this.evaluateAnswer(this.flashcard, [], this.rawAnswerData || {});

      const flashcardStatusId = (this.rawAnswerData)
        ? this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id
        : this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'].id;

      const params = {
        correct: correct,
        flashcardId: this.flashcard.id,
        flashcardStatusId,
        categoryId: this.currentCategoryId,
        rawAnswerData: this.rawAnswerData,
      };

      this.createFlashcardState(params);
      this.$analytics.events.updateSimulationAnswer({
        simulation: this.simulation,
        section: this.section,
        flashcard: this.flashcard,
        answerIds: this.answerIds,
        rawAnswerData: this.rawAnswerData,
      });
      return true;
    },
  },
};
</script>

<style scoped lang="scss">
.flashcard-content {
  display: grid;
  grid-template-columns: 1fr;
  max-width: 50rem;
  min-height: 100%;
  margin: 0 auto;

  &.two-column:has(.scenario-box, .asset-tray) {
    max-width: 100%;
    grid-template-columns: 1fr 1fr;
  }

  // second child has a border on the left
  > :nth-child(1) {
    border-right: 1px solid #d9d9d9;
  }

  > * {
    padding: 1.5rem;
  }

  .first-column {
    display: none;
    min-width: 40%;

    &:has(.scenario-box, .asset-tray) {
      display: block;
    }
  }
}

.first-column > :first-child {
  margin-top: 0;
}
</style>
